import {
  ChevronDownIcon,
  ChevronRightIcon,
  FileTextIcon,
  MapPinIcon,
  MountainIcon,
  NewspaperIcon,
  RouteIcon,
  TagIcon,
  UserIcon,
  type LucideIcon,
} from "lucide-react";
import Image from "next/image";

import {
  isFlatMegaPanel,
  isMenuHeading,
  isSitePath,
  megaLayoutFor,
  menuLinkGroups,
  type MegaLayout,
  type MenuItemSource,
  type MenuLinkGroup,
  type PublicMenu,
  type PublicMenuHeading,
  type PublicMenuItem,
  type PublicMenuItemLevel2,
  type PublicMenuItemLink,
} from "@tripcart/shared";
import {
  Collapsible,
  CollapsiblePanel,
  CollapsibleTrigger,
} from "@tripcart/traveler-ui/components/collapsible";
import { SheetClose } from "@tripcart/traveler-ui/components/sheet";
import { cx } from "@tripcart/traveler-ui/cx";

import { NavMenuLink } from "@/components/nav-menu-link.client";
import type { Translator } from "@/lib/translations";

import styles from "./nav-menu.module.css";

/**
 * A leaf's `source` records what kind of catalog row it was picked from
 * (menu.ts), so the icon is read off the menu the operator already saved —
 * never guessed from the label text, which would be wrong for any agency
 * whose destinations, tours or categories are not named like this one's.
 * `custom` and a missing `source` (a menu saved before the field existed) are
 * absent from the table on purpose: a lookup miss and "no icon" are the same
 * render, the same rule `TEXT_ICONS` in hf-glyphs.ts follows.
 */
const SOURCE_ICONS: Readonly<Record<MenuItemSource, LucideIcon | undefined>> = {
  tour: RouteIcon,
  destination: MapPinIcon,
  activity: MountainIcon,
  page: FileTextIcon,
  post: NewspaperIcon,
  author: UserIcon,
  category: TagIcon,
  custom: undefined,
};

function SourceIcon({
  source,
  className,
}: {
  source?: MenuItemSource;
  className?: string;
}): React.ReactElement | null {
  const Icon = source ? SOURCE_ICONS[source] : undefined;
  return Icon ? (
    <Icon aria-hidden className={className} strokeWidth={1.5} />
  ) : null;
}

/**
 * The operator's saved menu, rendered as the live site header (feature:
 * menu-builder). Structure mirrors `MenuPreviewDesktop` in operator-web one for
 * one — same three levels, same reveal rule, same three mega arrangements — so
 * what the builder previews is what a traveller gets.
 *
 * NO JAVASCRIPT OPENS ANYTHING. Panels are hidden with `visibility: hidden` and
 * revealed by their item's `:hover` and `:focus-within` (nav-menu.module.css).
 * That is what lets the header ship no menu library and add nothing to the
 * route's JS budget — product.md § Performance Requirements — while staying
 * keyboard-operable: focus reaches the trigger, `focus-within` opens the panel,
 * and the links inside are in tab order.
 *
 * Panels are absolutely positioned overlays, so opening one never reflows the
 * page and the header keeps its fixed 64px height whether an agency has a menu
 * or not (the CLS half of the same gate).
 *
 * A bar item belonging to the operator's menu carries the full header height,
 * so its hover state can be the brand-coloured rule along the bottom edge that
 * marks which panel is open. Items do not shrink: when the zone runs out of
 * room the bar wraps onto another line, so labels stay whole. Only a single
 * label wider than the whole bar ends in an ellipsis.
 */

/**
 * An operator's address is whatever they picked or typed, so it is not
 * necessarily an internal route: a real navigation carries `mailto:`, `tel:`,
 * `#anchor`, absolute URLs and the scheme-relative `//host` form alongside
 * paths. Only a path can be prefetched or client-navigated; everything else is
 * a plain anchor. The rule is the contract's own `isSitePath`, shared with
 * `hf-primitives.tsx`, so the two headers cannot drift.
 */
function isInternalPath(url: string): boolean {
  return isSitePath(url);
}

/**
 * Everything a caller passes reaches the anchor, `ref` included. That is not
 * decoration: `SheetClose render={…}` closes the sheet by merging its own
 * `onClick` and a ref onto the element it wraps, and a wrapper that keeps only
 * the props it names swallows both — the tap would navigate and leave the
 * sheet open over the new page. `href`, `target` and `rel` come last because
 * the item is the authority on where it goes.
 *
 * The scroll reset for internal links lives in `NavMenuLink`, so this file
 * stays renderable from Server Components (no function props).
 */
function MenuLink({
  item,
  children,
  ...props
}: React.ComponentPropsWithRef<"a"> & {
  item: { url: string; openInNewTab: boolean };
}): React.ReactElement {
  const external = item.openInNewTab
    ? { target: "_blank", rel: "noopener noreferrer" }
    : {};

  if (isInternalPath(item.url)) {
    return (
      <NavMenuLink {...props} href={item.url} {...external}>
        {children}
      </NavMenuLink>
    );
  }
  return (
    <a {...props} href={item.url} {...external}>
      {children}
    </a>
  );
}

/**
 * `fontSize` (px) is the Nav Menu element's size slider in the header builder.
 * The standard header omits it and keeps the bar's own type size.
 */
export function NavMenuBar({
  menu,
  fontSize,
}: {
  menu: PublicMenu;
  fontSize?: number;
}): React.ReactElement {
  // `align-items: stretch` + full-height anchors is what lets a bar item's
  // hover rule sit on the header's bottom edge. Items are spaced by their own
  // padding rather than a gap, so that rule spans the whole hover target.
  return (
    <ul className={styles.bar}>
      {menu.items.map((item) => (
        <NavMenuBarItem
          key={item.id}
          item={item}
          settings={menu.settings}
          fontSize={fontSize}
        />
      ))}
    </ul>
  );
}

function NavMenuBarItem({
  item,
  settings,
  fontSize,
}: {
  item: PublicMenuItem;
  settings: PublicMenu["settings"];
  fontSize?: number;
}): React.ReactElement {
  const hasChildren = item.children.length > 0;
  const isMega = item.mega && hasChildren;

  return (
    // Every panel — dropdown and mega alike — hangs from its own bar item, so
    // the cell is always the positioning context. A mega panel is a floating
    // card anchored to the item that opened it, not a strip across the header.
    <li className={styles.barItem}>
      {/* `aria-haspopup` is what tells a screen reader the panel exists — the
          chevron is decorative and the panel opens on hover/focus alone. */}
      <MenuLink
        item={item}
        className={styles.barLink}
        style={fontSize === undefined ? undefined : { fontSize }}
        aria-haspopup={hasChildren ? "true" : undefined}
      >
        <span className={styles.truncate}>{item.label}</span>
        {hasChildren ? (
          <ChevronDownIcon className={styles.barChevron} strokeWidth={1.75} />
        ) : null}
      </MenuLink>

      {isMega ? (
        <NavMegaPanel item={item} settings={settings} />
      ) : hasChildren ? (
        <NavDropdown item={item} />
      ) : null}
    </li>
  );
}

// FLUSH IS LOAD-BEARING. A panel's top edge meets the bar's bottom edge, which
// is the only reason the pointer can travel from one to the other. Any gap here
// — padding or margin — needs to stay inside the panel to keep the hover target
// contiguous; a margin would break it outright.
const ITEM_PANEL = cx(styles.itemReveal, styles.underBar);

function NavDropdown({ item }: { item: PublicMenuItem }): React.ReactElement {
  return (
    <div className={ITEM_PANEL}>
      <ul className={cx(styles.panel, styles.panelList, styles.dropdownPanel)}>
        {item.children.map((child) => (
          <li key={child.id} className={styles.subItem}>
            <MenuLink item={child} className={styles.row}>
              <span className={styles.leafLabel}>
                <SourceIcon source={child.source} className={styles.leafIcon} />
                <span className={styles.truncate}>{child.label}</span>
              </span>
              {child.children.length > 0 ? (
                <ChevronRightIcon
                  className={styles.rowChevron}
                  strokeWidth={1.75}
                />
              ) : null}
            </MenuLink>

            {child.children.length > 0 ? (
              <NavSubDropdown child={child} />
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function NavSubDropdown({
  child,
}: {
  child: PublicMenuItemLevel2;
}): React.ReactElement {
  return (
    <div className={cx(styles.subReveal, styles.besideRow)}>
      <ul className={cx(styles.panel, styles.panelList, styles.subPanel)}>
        {/* A heading is a label row between the links, never a link. */}
        {child.children.flatMap((node) =>
          isMenuHeading(node)
            ? [
                <li key={node.id}>
                  <p className={styles.subPanelHeading}>{node.label}</p>
                </li>,
                ...node.children.map((leaf) => (
                  <NavSubDropdownRow key={leaf.id} leaf={leaf} />
                )),
              ]
            : [<NavSubDropdownRow key={node.id} leaf={node} />],
        )}
      </ul>
    </div>
  );
}

function NavSubDropdownRow({
  leaf,
}: {
  leaf: PublicMenuItemLink;
}): React.ReactElement {
  return (
    <li>
      <MenuLink item={leaf} className={styles.row}>
        <span className={styles.leafLabel}>
          <SourceIcon source={leaf.source} className={styles.leafIcon} />
          <span className={styles.truncate}>{leaf.label}</span>
        </span>
      </MenuLink>
    </li>
  );
}

function NavMegaPanel({
  item,
  settings,
}: {
  item: PublicMenuItem;
  settings: PublicMenu["settings"];
}): React.ReactElement {
  const megaLayout = megaLayoutFor(item, settings);

  // Flush under the bar, for the same hover-target reason as the dropdown.
  if (megaLayout === 3) {
    return (
      <div className={ITEM_PANEL}>
        {isFlatMegaPanel(item.children) ? (
          // No columns means no sidebar to stack headings down: the children
          // are the links, so they fill the card on their own — the same
          // flattening layouts 1 and 2 do, drawn as layout 3 draws a link.
          <div className={cx(styles.panel, styles.flatPanel)}>
            <NavMegaLeaves leaves={item.children} />
          </div>
        ) : (
          <NavMegaSidebar item={item} />
        )}
      </div>
    );
  }

  return (
    <div className={ITEM_PANEL}>
      <div className={cx(styles.panel, styles.columnsPanel)}>
        <NavMegaColumns item={item} megaLayout={megaLayout} />
      </div>
    </div>
  );
}

/**
 * Headings across, their links beneath — layouts 1 and 2, which differ only in
 * what a link IS: a bulleted line of text, or the linked catalog item's own
 * picture beside its title.
 *
 * TWO LEVELS RENDER AS ONE FLAT COLUMN. A mega item whose children have no
 * children of their own has nothing to head each column with, and a heading per
 * link would read as a column of one-item columns. So the children become the
 * links themselves — the arrangement the operator's tree actually describes.
 */
function NavMegaColumns({
  item,
  megaLayout,
}: {
  item: PublicMenuItem;
  megaLayout: MegaLayout;
}): React.ReactElement {
  const Leaf = megaLayout === 2 ? NavMegaImageLink : NavMegaTextLink;
  // Image rows need more air between them than text rows: a 40px-tall thumbnail
  // separated by 2px reads as one striped block rather than as distinct links.
  const images = megaLayout === 2 ? "" : undefined;
  const columns = item.children;

  if (isFlatMegaPanel(item.children)) {
    return (
      <ul className={styles.rows} data-images={images}>
        {columns.map((column) => (
          <li key={column.id}>
            <Leaf leaf={column} />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className={styles.columns}>
      {columns.map((column) => (
        <div key={column.id} className={styles.column}>
          <NavColumnHeading column={column} />
          {/* A heading inside a column splits it into labelled runs. */}
          {menuLinkGroups<MegaLeafItem>(column.children).map((group, index) => (
            <div
              key={group.heading?.id ?? `loose-${index}`}
              className={styles.columnGroup}
            >
              {group.heading ? (
                <p className={styles.groupHeading}>{group.heading.label}</p>
              ) : null}
              <ul className={styles.rows} data-images={images}>
                {group.links.map((leaf) => (
                  <li key={leaf.id}>
                    <Leaf leaf={leaf} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Layout 3 — headings down the left, the pointed-at one's links beside them.
 *
 * The builder's preview illustrates this with the FIRST heading statically
 * selected, because a read-only mock has nothing to select with. A live header
 * cannot copy that: every link the operator put in the panel has to be
 * reachable, and freezing on column one would bury the rest.
 *
 * EACH HEADING AFTER THE FIRST OWNS ITS OWN PANEL, rendered inside that
 * heading's `<li>` and positioned into the area on the right. That co-location
 * is the whole trick — a panel is revealed by the `:hover` of an element it
 * sits inside, so a separate right-hand column could never respond to a
 * heading in a separate list without `:has()` or script.
 *
 * A HEADING WITH NO LINKS IS A PLAIN LINK: no chevron, and an empty panel that
 * covers the resting links, so pointing at it never shows another column's
 * links beside it. It still renders — it is a link the operator added.
 *
 * THE RESTING COLUMN (the first one with links) GETS NO PANEL OF ITS OWN,
 * because it is already the resting state below: its links sit in the flow on
 * the right so the area is never blank before the traveller points at anything,
 * and every other heading's panel is opaque and covers them. Giving it one too
 * rendered its links a second time —
 * both copies visible, and so both in the tab order and the accessibility tree
 * whenever focus sat inside the panel, which made a keyboard traveller pass
 * through the same links twice and a screen reader announce them twice.
 *
 * THE RIGHT-HAND AREA STACKS EVERY COLUMN, not just the resting one. Those
 * panels are absolutely positioned, so they add no height of their own — the
 * card's height comes from whatever sits in the flow beside the sidebar. With
 * only the resting column there, a short first column (a plain text list, say)
 * sizes the whole card, and pointing at a heading whose links carry pictures
 * gets that panel cut off mid-image by the `overflow: hidden` below. Stacking
 * every column into one grid cell makes the card as tall as the TALLEST of
 * them, so no panel can overflow it and the card never resizes as the traveller
 * moves between headings.
 */
function NavMegaSidebar({
  item,
}: {
  item: PublicMenuItem;
}): React.ReactElement {
  const withLinks = item.children.filter(
    (column) => column.children.length > 0,
  );
  const resting = withLinks[0]!;

  return (
    // `overflow: hidden` so the sidebar's own edge is clipped by the card's
    // radius, and the panels absolutely positioned below resolve against this
    // element — hence `position: relative` here and nowhere inside.
    <div className={cx(styles.panel, styles.sidebarCard)}>
      <ul className={styles.sidebarList}>
        {item.children.map((column) => (
          <li key={column.id} className={styles.sidebarColumn}>
            <MenuLink item={column} className={styles.sidebarEntry}>
              <span className={styles.leafLabel}>
                <SourceIcon
                  source={column.source}
                  className={styles.leafIcon}
                />
                <span className={styles.truncate}>{column.label}</span>
              </span>
              {column.children.length > 0 ? (
                <ChevronRightIcon
                  className={styles.sidebarChevron}
                  strokeWidth={2.2}
                />
              ) : null}
            </MenuLink>

            {/* The panel starts exactly where the sidebar ends (see
                `.columnPanel`). The resting column needs none — the stack
                below is already showing its links. */}
            {column === resting ? null : (
              <div className={styles.columnPanel}>
                {column.children.length > 0 ? (
                  <NavMegaLeaves leaves={column.children} />
                ) : null}
              </div>
            )}
          </li>
        ))}
      </ul>

      {/* One grid cell, every column with links in it. Only the resting one
          is shown — the rest are inert sizers, and `visibility: hidden` keeps
          their links out of the tab order and the accessibility tree rather
          than merely transparent. */}
      <div className={styles.stack}>
        {withLinks.map((column) => (
          <div
            key={column.id}
            className={styles.stackCell}
            data-sizer={column === resting ? undefined : ""}
          >
            <NavMegaLeaves leaves={column.children} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The links filling layout 3's panel — one heading's children, or a flat mega
 * item's own children when there are no headings to group them under.
 *
 * Links that carry pictures become the prototype's card grid; links with none
 * stay a two-column text list, because a grid of bare titles is what that grid
 * would otherwise degrade into. The operator's own catalog decides which,
 * exactly as it does for layout 2.
 *
 * No heading of its own: the sidebar entry beside a panel already names it, and
 * repeating it over the links reads as the same column titled twice.
 */
function NavMegaLeaves({
  leaves: nodes,
}: {
  leaves: readonly (MegaLeafItem | PublicMenuHeading)[];
}): React.ReactElement {
  const groups = menuLinkGroups<MegaLeafItem>(nodes);
  if (groups.some((group) => group.heading)) {
    return <NavMegaGroups groups={groups} />;
  }

  const leaves = groups.flatMap((group) => group.links);
  const illustrated = leaves.some((leaf) => leaf.imageUrl);

  return illustrated ? (
    <ul className={styles.leafGridCards}>
      {leaves.map((leaf) => (
        <li key={leaf.id}>
          <NavMegaCard leaf={leaf} />
        </li>
      ))}
    </ul>
  ) : (
    <ul className={styles.leafGridText}>
      {leaves.map((leaf) => (
        <li key={leaf.id}>
          <NavMegaTextLink leaf={leaf} />
        </li>
      ))}
    </ul>
  );
}

/**
 * A layout-3 panel the operator grouped with headings: each heading in bold
 * over its links. A heading is a label and nothing else — no address, so it
 * renders as text a traveller cannot click, which is what tells it apart from
 * the links beneath it. Loose links between headings keep their place as an
 * untitled group.
 *
 * Pictures follow the same rule as an ungrouped layout-3 panel: when any link
 * carries one, every group draws the card grid, one group per row so the cards
 * keep their width; when none does, the groups are text lists, two to a row.
 */
function NavMegaGroups({
  groups,
}: {
  groups: readonly MenuLinkGroup<MegaLeafItem>[];
}): React.ReactElement {
  const illustrated = groups.some((group) =>
    group.links.some((leaf) => leaf.imageUrl),
  );

  return (
    <div
      className={styles.groupGrid}
      data-images={illustrated ? "" : undefined}
    >
      {groups.map((group, index) => (
        <div key={group.heading?.id ?? `loose-${index}`}>
          {group.heading ? (
            <p className={styles.groupHeading}>{group.heading.label}</p>
          ) : null}
          {illustrated ? (
            <ul className={styles.leafGridCards}>
              {group.links.map((leaf) => (
                <li key={leaf.id}>
                  <NavMegaCard leaf={leaf} />
                </li>
              ))}
            </ul>
          ) : (
            <ul className={styles.groupLinks}>
              {group.links.map((leaf) => (
                <li key={leaf.id}>
                  <MenuLink item={leaf} className={styles.megaLink}>
                    <SourceIcon
                      source={leaf.source}
                      className={styles.leafIcon}
                    />
                    <span className={styles.truncate}>{leaf.label}</span>
                  </MenuLink>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The same menu in the mobile sheet. Nesting becomes indentation rather than a
 * panel, and a parent's children start folded away: listing every sub-item at
 * once made the sheet several screens long (TC-65 #11). A parent stays a link
 * to its own page; the chevron beside it is a separate button that unfolds its
 * children — the same shape the builder's mobile preview draws. `mega` is a
 * desktop panel arrangement and has nothing to render here.
 *
 * Every link closes the sheet on tap (`SheetClose`), matching the stock nav.
 */
export function NavMenuMobile({
  menu,
  t,
}: {
  menu: PublicMenu;
  t: Translator;
}): React.ReactElement {
  return (
    <ul className={styles.mobileList}>
      {menu.items.map((item) => (
        <li key={item.id}>
          <NavMobileBranch item={item} depth={0} t={t}>
            {item.children.map((child) => (
              <NavMobileBranch key={child.id} item={child} depth={1} t={t}>
                {child.children.map((leaf) =>
                  isMenuHeading(leaf) ? (
                    <div key={leaf.id}>
                      <p className={styles.mobileHeading}>{leaf.label}</p>
                      {leaf.children.map((link) => (
                        <NavMobileRow key={link.id} item={link} depth={3} />
                      ))}
                    </div>
                  ) : (
                    <NavMobileRow key={leaf.id} item={leaf} depth={2} />
                  ),
                )}
              </NavMobileBranch>
            ))}
          </NavMobileBranch>
        </li>
      ))}
    </ul>
  );
}

/** A row that has children: the link, a toggle beside it, and the children
 *  folded beneath until the toggle opens them. The toggle says what it opens
 *  ("Show Tours sub-menu"), so a screen reader does not hear the link's name
 *  twice, and announces open or closed through `aria-expanded`. */
function NavMobileBranch({
  item,
  depth,
  t,
  children,
}: {
  item: PublicMenuItem | PublicMenuItemLevel2;
  depth: 0 | 1;
  t: Translator;
  children: React.ReactNode;
}): React.ReactElement {
  if (item.children.length === 0) {
    return <NavMobileRow item={item} depth={depth} />;
  }
  return (
    <Collapsible>
      <div className={styles.mobileBranchHead}>
        <NavMobileRow item={item} depth={depth} />
        <CollapsibleTrigger chevron className={styles.mobileToggle}>
          <span className="tui-sr-only">
            {t("Show {label} sub-menu", { label: item.label })}
          </span>
        </CollapsibleTrigger>
      </div>
      <CollapsiblePanel>{children}</CollapsiblePanel>
    </Collapsible>
  );
}

function NavMobileRow({
  item,
  depth,
}: {
  item: PublicMenuItem | PublicMenuItemLevel2 | PublicMenuItemLink;
  depth: 0 | 1 | 2 | 3;
}): React.ReactElement {
  return (
    <SheetClose
      nativeButton={false}
      render={
        <MenuLink item={item} className={styles.mobileRow} data-depth={depth}>
          {item.label}
        </MenuLink>
      }
    />
  );
}

/**
 * A column heading: a quiet, small-caps eyebrow label, lit in the agency's
 * brand colour only on hover. It is a link in its own right — the heading is
 * a level-2 item with an address of its own. Its icon reads the same `source`
 * every leaf under it does (see `SOURCE_ICONS`).
 */
function NavColumnHeading({
  column,
}: {
  column: PublicMenuItemLevel2;
}): React.ReactElement {
  return (
    <MenuLink item={column} className={styles.heading}>
      <SourceIcon source={column.source} className={styles.headingIcon} />
      <span className={styles.truncate}>{column.label}</span>
    </MenuLink>
  );
}

/** A mega-panel link: level 3 normally, level 4 under a heading, level 2 in the
 *  flattened two-level case `isFlatMegaPanel` picks out. */
type MegaLeafItem = PublicMenuItemLevel2 | PublicMenuItemLink;

/**
 * A column link in layout 1 and the plain-text mega/leaf-grid lists: the row
 * itself carries the pick-out signal (an accent tick that grows in from the
 * left on hover, `.megaText` in nav-menu.module.css) rather than a marker
 * sitting in front of the label. Its icon reads the leaf's own `source`.
 */
function NavMegaTextLink({ leaf }: { leaf: MegaLeafItem }): React.ReactElement {
  return (
    <MenuLink
      item={leaf}
      className={cx(styles.megaLink, styles.megaText)}
      data-leaf-style="text"
    >
      <SourceIcon source={leaf.source} className={styles.leafIcon} />
      <span className={styles.truncate}>{leaf.label}</span>
    </MenuLink>
  );
}

/**
 * A column link in layout 2 — the linked catalog item's featured image beside
 * its title, which is the whole point of the arrangement.
 *
 * NO IMAGE MEANS NO THUMBNAIL, not an empty frame. An operator's catalog is
 * routinely part-illustrated (a destination with a photo next to a page that
 * cannot have one), and reserving the space anyway turns "not filled in yet"
 * into "broken". The row falls back to its title and closes the gap.
 *
 * `alt=""` because the label is right there: naming the picture too would make
 * a screen reader say every link twice.
 */
function NavMegaImageLink({
  leaf,
}: {
  leaf: MegaLeafItem;
}): React.ReactElement {
  return (
    <MenuLink
      item={leaf}
      className={cx(styles.megaLink, styles.megaImage)}
      data-leaf-style="image"
    >
      {leaf.imageUrl ? (
        <span className={styles.thumb}>
          <Image
            src={leaf.imageUrl}
            alt=""
            width={56}
            height={40}
            sizes="56px"
            className={styles.thumbImage}
          />
        </span>
      ) : null}
      <span className={styles.truncate}>{leaf.label}</span>
    </MenuLink>
  );
}

/** A layout-3 card: the picture above its title, and the title alone when the
 *  catalog row has no picture — the same rule as layout 2. */
function NavMegaCard({ leaf }: { leaf: MegaLeafItem }): React.ReactElement {
  return (
    <MenuLink item={leaf} className={styles.card} data-leaf-style="card">
      {leaf.imageUrl ? (
        <span className={styles.cardThumb}>
          <Image
            src={leaf.imageUrl}
            alt=""
            width={200}
            height={72}
            sizes="200px"
            className={styles.thumbImage}
          />
        </span>
      ) : null}
      <span className={styles.cardLabel}>{leaf.label}</span>
    </MenuLink>
  );
}
